// ═══════════════════════════════════════════════════════════════════════════
// FILE: src/app/services/agent-status.service.ts
// DATE: 2026-11-02
// CREATOR: PIA (Rolle HUGO), beauftragt vom PO am 02.11.2026 („passt, bau los“)
// ceeq.it: _40 · KI-Agent-Statusanzeige, Schritt 2+3
//
// PURPOSE
//   Ist die KI in diesem pslot wach? Lädt GET /api/agents/status?pslot= und hält das
//   Ergebnis je pslot. Zwei Nutzer:
//     ▸ app-agent-status (die Ampelpunkte unten rechts auf jeder Projektseite)
//     ▸ app-ask-panel    (der Punkt je Agent in der @-Auswahl von Alt+P)
//   Takt POLL_MS, solange eine Projektseite offen ist. Der Wächter auf der Agent-VM meldet
//   sich alle 30–60 s; schneller abzufragen brächte nichts.
//
// ENDPUNKT  nodenanomvp/src/routes/agents-status.route.js · Ampel-Regeln utils/agentAmpel.js
// ═══════════════════════════════════════════════════════════════════════════

import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export type AgentLight = 'green' | 'yellow' | 'red';

export interface AgentStatus {
  uuid: string;
  handle: string | null;
  display_name: string;
  light: AgentLight;
  reason: string;
  hint: string;
  open_requests: number;
  oldest_request_at: string | null;
  last_seen_at: string | null;
}

const POLL_MS = 60_000;

@Injectable({ providedIn: 'root' })
export class AgentStatusService {
  private http = inject(HttpClient);
  private readonly api = this.buildApiBase();

  /** pslot der offenen Projektseite — gesetzt von app-agent-status. */
  readonly pslot = signal<string | null>(null);
  /** Stand je pslot; die Ask-Auswahl liest auch andere pslots als den der Seite. */
  private readonly byPslot = signal<Record<string, AgentStatus[]>>({});
  /** false, solange das Backend den Endpunkt nicht kennt (404 = älter als v120) — dann bleibt alles still. */
  readonly available = signal(true);

  readonly agents = computed(() => this.byPslot()[this.pslot() || ''] || []);
  readonly worst = computed<AgentLight | null>(() => {
    const a = this.agents();
    if (a.length === 0) return null;
    return a.some((x) => x.light === 'green') ? 'green' : a.some((x) => x.light === 'yellow') ? 'yellow' : 'red';
  });
  readonly waiting = computed(() => this.agents().reduce((s, a) => s + a.open_requests, 0));

  private timer: ReturnType<typeof setInterval> | null = null;

  /** Gleiche Ableitung wie in den übrigen Diensten (requests.service.ts). */
  private buildApiBase(): string {
    const host = window.location.hostname;
    if (host.includes('localhost') || host === '127.0.0.1') return 'http://localhost:3000';
    return `https://api.${host}`;
  }

  /** Seite gewechselt: neuen pslot setzen, sofort laden, Takt starten oder anhalten. */
  follow(pslot: string | null): void {
    if (pslot === this.pslot()) return;
    this.pslot.set(pslot);
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
    if (!pslot) return;
    void this.load(pslot);
    this.timer = setInterval(() => { const p = this.pslot(); if (p) void this.load(p); }, POLL_MS);
  }

  /** Einmal laden — auch für einen pslot, der nicht der Seite gehört (Ask-Panel). */
  async load(pslot: string): Promise<AgentStatus[]> {
    if (!this.available()) return [];
    try {
      const params = new HttpParams().set('pslot', pslot);
      const r = await firstValueFrom(this.http.get<{ agents: AgentStatus[] }>(
        `${this.api}/api/agents/status`, { params, withCredentials: true }));
      const liste = r.agents || [];
      this.byPslot.update((m) => ({ ...m, [pslot]: liste }));
      console.log('[AgentStatusService] pslot=%s agenten=%d grün=%d offen=%d', pslot, liste.length,
        liste.filter((a) => a.light === 'green').length, liste.reduce((s, a) => s + a.open_requests, 0));
      return liste;
    } catch (e: any) {
      if (e?.status === 404) {
        console.warn('[AgentStatusService] /api/agents/status fehlt (Backend älter als v120) — Anzeige aus');
        this.available.set(false);
      } else {
        console.warn('[AgentStatusService] Status nicht geladen pslot=%s status=%s', pslot, e?.status);
      }
      return [];
    }
  }

  /** Ampel eines Agenten in einem pslot — null, wenn (noch) unbekannt. */
  of(pslot: string | null, uuid: string): AgentStatus | null {
    if (!pslot) return null;
    return (this.byPslot()[pslot] || []).find((a) => a.uuid === uuid) || null;
  }
}

/** „3 min ago“ · „2 h ago“ — für Lebenszeichen und älteste Anfrage. */
export function agoOf(iso: string | null): string {
  if (!iso) return 'never';
  const s = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (s < 90) return `${s} s ago`;
  if (s < 5400) return `${Math.round(s / 60)} min ago`;
  if (s < 172800) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
}
